import React, { useEffect, useRef, useCallback } from "react";
import { Paperclip, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AttachmentChip } from "./AttachmentChip";
import { AttachmentItem } from "../../hooks/useAttachments";
import { TokenInfo } from "../../hooks/useTokenCount";
import { TokenCounter } from "./TokenCounter";
import { useSubscriptionGate } from "../../hooks/useSubscriptionGate";
import chatButtonOnSvg from "../../assets/svg/chatButtonOn.svg";
import chatButtonOffSvg from "../../assets/svg/chatButtonOff.svg";

interface ChatFooterProps {
  value: string;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onValueChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onFocusChange: (isFocused: boolean) => void;
  onPaste: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  canSend: boolean;
  attachments: AttachmentItem[];
  onRemoveAttachment: (id: string) => void;
  onFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  isDragActive: boolean;
  tokenInfo: TokenInfo;
  composerHasFocus: boolean;
}

export const ChatFooter: React.FC<ChatFooterProps> = ({
  value,
  textareaRef,
  onValueChange,
  onKeyDown,
  onFocusChange,
  onPaste,
  onSend,
  canSend,
  attachments,
  onRemoveAttachment,
  onFileInputChange,
  onDrop,
  onDragOver,
  onDragLeave,
  isDragActive,
  tokenInfo,
  composerHasFocus,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const localTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const navigate = useNavigate();
  const { isFree } = useSubscriptionGate();

  useEffect(() => {
    if (textareaRef?.current == null && localTextareaRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (textareaRef as any).current = localTextareaRef.current;
    }
  }, [textareaRef]);

  const handleAttachmentClick = () => fileInputRef.current?.click();

  const syncHeight = useCallback(() => {
    const ta = textareaRef?.current || localTextareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  }, [textareaRef]);

  useEffect(() => {
    syncHeight();
  }, [value, attachments.length, syncHeight]);

  return (
    <div
      className="relative w-full"
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      {/* Drag overlay */}
      {isDragActive && (
        <div className="absolute inset-0 z-20 rounded-2xl border-2 border-dashed border-[#2C405F] bg-[#FAF6F1]/80 backdrop-blur-sm text-center text-sm font-medium text-[#2C405F] flex items-center justify-center">
          <div>
            <p>Drop files to attach</p>
            <p className="text-xs text-[#8E8378]">
              Supported: .txt .md .json .csv .pdf
            </p>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".txt,.md,.markdown,.json,.csv,.pdf,.png,.jpg,.jpeg,.gif,.webp,.svg"
        className="hidden"
        onChange={onFileInputChange}
      />

      {/* Bubble */}
      <div
        className={`rounded-[12px] bg-[#FDFAF7] dark:bg-[#2C405F] px-3 pt-3 pb-0 transition-all ${
          composerHasFocus
            ? "border-[#2C405F] shadow-[0_6px_18px_-6px_#00000030]"
            : "border-[#E5DDD3] shadow-[0_3px_10px_-3px_#00000020]"
        }`}
      >
        {/* Attachments row */}
        {attachments.length > 0 && (
          <div className="mb-3 flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
            {attachments.map((a) => (
              <AttachmentChip
                key={a.id}
                attachment={a}
                onRemove={onRemoveAttachment}
              />
            ))}
          </div>
        )}

        <div className="relative w-full">
          <div className="w-full pb-[56px]">
            <textarea
              ref={(el) => {
                localTextareaRef.current = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if (textareaRef) (textareaRef as any).current = el;
              }}
              rows={1}
              value={value}
              onChange={(e) => {
                onValueChange(e.target.value);
                requestAnimationFrame(syncHeight);
              }}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              onFocus={() => onFocusChange(true)}
              onBlur={() => onFocusChange(false)}
              placeholder="Ask me about anything: incidents, logs, risks, etc."
              className="chat-input w-full resize-none overflow-y-auto bg-[#FDFAF7] dark:bg-[#2C405F] text-[#1D1B19] dark:text-[#E3E6EA] placeholder-[#B7AB9E] dark:placeholder-[#9AAABF] outline-none min-h-[40px] max-h-[160px]"
              style={{
                fontFamily: "Sofia Pro, sans-serif",
                fontWeight: 300,
                fontSize: "16px",
                lineHeight: "125%",
                letterSpacing: "0%",
                verticalAlign: "middle",
                transition: "height 160ms ease",
              }}
            />
          </div>

          {/* Toolbar pinned to bottom inside the bubble */}
          <div className="absolute left-0 right-0 bottom-2 flex items-center justify-between h-[40px]">
            <button
              type="button"
              onClick={handleAttachmentClick}
              aria-label="Attach supporting files"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#F3ECE4] dark:bg-[#455D7D] text-[#2C405F] dark:text-[#E3E6EA] hover:bg-[#f5efe6] dark:hover:bg-[#6E829E] hover:shadow-sm hover:cursor-pointer transition"
            >
              <Paperclip size={18} />
            </button>

            <div className="flex items-center gap-2">
              <TokenCounter tokenInfo={tokenInfo} />
              {isFree && (
                <button
                  type="button"
                  onClick={() => navigate("/pricing")}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#E8922A] hover:bg-[#D4831F] px-3 h-9 text-white text-[12px] font-semibold transition-colors cursor-pointer border-none whitespace-nowrap"
                  style={{ fontFamily: "Sofia Pro, sans-serif" }}
                >
                  <Crown size={14} />
                  Upgrade
                </button>
              )}
              <button
                type="button"
                onClick={onSend}
                disabled={!canSend}
                aria-label="Send message"
                className="relative inline-flex items-center justify-center transition-opacity"
                style={{ background: "transparent", padding: 0 }}
              >
                <img
                  src={canSend ? chatButtonOnSvg : chatButtonOffSvg}
                  alt="Send"
                  className="h-9 w-9"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
